import { SHAPE_STYLES, SHADOWS } from './constants';
import { COLORS } from './constants-colors';
import {
  addGameObject,
  anchoredPos,
  getZeroStaticEntity,
  setEntityPosition,
  setParent,
  getEntityPositionXY,
  getZeroAnchoredEntity,
  getFirstObjectByType,
} from './entity';
import { getImageUriBySize, hexToColor, lerpNumber, multiplyColorAlpha255, v2 } from './helpers';
import { dt } from '.';
import { ObjectType, ServerEvent_EntityProps, ServerEvent_ImageObject } from './shared/api_shared_types';
import * as JourneyMapModule from './journey_map';
import { JOURNEY_MAP_ANCHOR_RELEASE_THRESHOLD_PX } from './constants';
import { setVisibilityState, setParentByEntityProps } from './simulate';
import {
  Entity,
  JourneyMap,
  VisibilityState,
  GameManager,
  GameObjectController,
  ImageObject,
  MoveMode,
  RenderItemType,
  Shape,
  SharedObjectType,
  type V2,
} from './types';
import { createAndAddLocalTextObject2 } from './text_object2/text_object2';
import { createImageEnt } from './image_entity';
import * as DataController from './controllers/data_controller';
import logger from './controllers/logger';

// Client-only build has no API host; CMS-image branches below treat every URI as local.
const API_HOST = '';

const POLAROID_PADDING = 32; // uniform padding on top and sides
const POLAROID_BOTTOM = 128; // extra strip at the bottom (the polaroid tab, holds the caption)
import {
  EMPTY_RENDER_TRANSFORM,
  type RenderPackageItem,
  type RenderPackageRect,
} from './render_package';

let lastClickedObject: ImageObject | null = null;

export function getAndClearLastClicked(): ImageObject | null {
  const result = lastClickedObject;
  lastClickedObject = null;
  return result;
}

export function controller(): GameObjectController {
  return {
    getNew: getZeroImageObject,
    sync,
    update,
    createPackage,
  };
}

export function getZeroImageObject(ent: Entity): ImageObject {
  let newImageObject: ImageObject = {
    ...ent,
    objectType: SharedObjectType.IMAGE_OBJECT,

    label: '',
    imageUri: '',

    maxImageWidthEm: 0,
    minImageWidthEm: 0,
    maxImageHeightEm: 0,
    fixedSize: false,

    imageEnt: null,
    previousImageEnt: null,
    textObject: null,

    requestImageUpload: false,
    isWaitingForResponse: false,
    uploadedImageId: '',
    uploadedImageUrl: '',

    backgroundColor: COLORS.TRANSPARENT,

    baseRotation: 0,
    baseScale: 1,
    liftScale: 0,
    liftScaleTarget: 0,
    liftRotation: 0,
    liftRotationTarget: 0,
    liftSettleTimer: null,

    journeyMapAnchorIndex: null,
  };

  return newImageObject;
}

export function sync(
  manager: GameManager,
  imageObject: ImageObject,
  data: ServerEvent_ImageObject,
) {
  let entityProps: ServerEvent_EntityProps = {};

  if (data.hasOwnProperty('entityProps')) {
    entityProps = data.entityProps;
  }

  if (data.hasOwnProperty('maxImageWidthEm')) {
    imageObject.maxImageWidthEm = data.maxImageWidthEm;
  }

  if (data.hasOwnProperty('maxImageHeightEm')) {
    imageObject.maxImageHeightEm = data.maxImageHeightEm;
  }

  if (data.hasOwnProperty('minImageWidthEm')) {
    imageObject.minImageWidthEm = data.minImageWidthEm || 1.5;
  }

  if (data.hasOwnProperty('backgroundHexColor')) {
    const { valid, color } = hexToColor(data.backgroundHexColor);
    if (valid) {
      imageObject.backgroundColor = color;
    }
  }

  if (data.hasOwnProperty('fixedSize')) {
    imageObject.fixedSize = data.fixedSize;
  }

  if (!imageObject.maxImageWidthEm && !imageObject.maxImageHeightEm) {
    imageObject.maxImageWidthEm = 1.5;
    imageObject.maxImageHeightEm = 1.5;
  }

  if (!imageObject.id) {
    addGameObject(manager, imageObject);

    let appearDuration = 0.8;
    if (data.hasOwnProperty('appearImmediately')) {
      if (data.appearImmediately) {
        appearDuration = 0;
      }
    }
    initImageObject(
      manager,
      imageObject,
      data.label,
      data.imageUri,
      data.clientImageId,
      appearDuration,
    );

    setParentByEntityProps(manager, imageObject, data.sId, data.roomId, entityProps);
  } else {
    if (data.hasOwnProperty('imageUri')) {
      changeImageUri(imageObject, data.imageUri);
      //change image
    }
  }

  if (data.hasOwnProperty('noHover')) {
    imageObject.allowHighlight = !data.noHover;
    imageObject.imageEnt.allowHighlight = !data.noHover;
  }
}

export function createAndAddLocalImageObject(
  manager: GameManager,
  ent: Entity,
  imageId: string,
  props: Partial<ImageObject> = {},
): ImageObject {
  const imageObject = { ...getZeroImageObject(ent), ...props };
  imageObject.baseRotation = imageObject.rotation;
  addGameObject(manager, imageObject);
  initImageObject(manager, imageObject, imageObject.label, '', imageId, imageObject.appearDuration);
  return imageObject;
}

export function initImageObject(
  manager: GameManager,
  imageObject: ImageObject,
  label: string,
  imageUri: string,
  clientImageId: string,
  appearDuration = 0.8,
) {
  imageObject.shape = Shape.RECT;
  imageObject.label = label;
  imageObject.imageUri = imageUri;
  imageObject.allowHighlight = true;
  imageObject.allowDrag = true;
  imageObject.clickable = true;
  imageObject.alwaysOnTop = true;
  imageObject.appearDuration = appearDuration;
  imageObject.disappearDuration = 0.4;

  const length = API_HOST.length;

  let imageUriSized = imageUri;
  if (clientImageId) {
    imageUriSized = clientImageId;
  } else if (imageUri.substring(0, length) === API_HOST) {
    if (imageObject.maxImageWidthEm < 2 && imageObject.maxImageHeightEm < 2) {
      imageUriSized = getImageUriBySize(imageUri, 'small');
    } else if (imageObject.maxImageWidthEm < 5 && imageObject.maxImageHeightEm < 5) {
      imageUriSized = getImageUriBySize(imageUri, 'medium');
    } else {
      imageUriSized = getImageUriBySize(imageUri, 'large');
    }

    if (DataController.getImageState(imageUriSized) === '') {
      DataController.cacheImageFromApiAsync(manager.user.accessToken, imageUriSized, imageUriSized);
    }
  } else {
    if (DataController.getImageState(imageUriSized) === '') {
      DataController.cacheImage(imageUri, imageUriSized);
    }
  }

  imageObject.imageEnt = createImageEnt(manager, getZeroAnchoredEntity(), imageUriSized, {
    appearOnLoad: true,
    appearStyle: appearDuration ? 'fade_and_scale' : 'none',
  });
  if (imageObject.fixedSize) {
    imageObject.imageEnt.disableAutoSize = true;
  }

  imageObject.imageEnt.anchoredPos = anchoredPos(v2(0.5, 0), v2(0, POLAROID_PADDING), v2(0.5, 0));
  imageObject.imageEnt.appearDuration = appearDuration;
  imageObject.imageEnt.disappearDuration = 0.4;
  imageObject.imageEnt.maxWidthEm = imageObject.maxImageWidthEm;
  imageObject.imageEnt.maxHeightEm = imageObject.maxImageHeightEm;
  imageObject.imageEnt.allowDrag = true;
  imageObject.imageEnt.passDrag = true;
  imageObject.imageEnt.allowHighlight = true;
  imageObject.imageEnt.passHover = true;
  imageObject.imageEnt.clickable = true;
  imageObject.imageEnt.passClick = true;
  imageObject.imageEnt.passClickCanvas = true;

  if (label) {
    // Wrap width is set up front so the caption never lays out at zero width while the photo loads.
    const labelWidth = imageObject.maxImageWidthEm * manager.rootUnitSizePx;
    imageObject.textObject = createAndAddLocalTextObject2(manager, getZeroAnchoredEntity(), {
      text: label,
      textOverflow: 'wrap',
      width: labelWidth,
      padding: [8, 12, 8, 12],
      defaultTextStyle: {
        color: COLORS.NEW_STYLE.BLUE_DARK,
        fontSize: 48,
        fontId: 'font_bold',
        lineHeight: 1.1,
      },
    });
    imageObject.textObject.allowDrag = false;
    imageObject.textObject.passDrag = true;
    imageObject.textObject.allowHighlight = true;
    imageObject.textObject.passHover = true;
    imageObject.textObject.clickable = true;
    imageObject.textObject.passClick = true;
    imageObject.textObject.moveMode = MoveMode.ANCHORED;
    // Center the caption within the white bottom strip, relative to the photo's bottom edge.
    imageObject.textObject.anchoredPos = anchoredPos(
      v2(0.5, 1),
      v2(0, -45),
      v2(0.5, 0),
      imageObject.imageEnt.id,
    );
  }

  const imHeight =
    imageObject.imageEnt.height || imageObject.maxImageHeightEm * manager.rootUnitSizePx;
  setParent(imageObject.imageEnt, imageObject);
  if (imageObject.textObject) {
    setParent(imageObject.textObject, imageObject, v2(0, imHeight / 2));
  }

  // The caption lives inside the fixed bottom strip, so it does not add to the card height.
  const totalHeight = imHeight + POLAROID_PADDING + POLAROID_BOTTOM;
  imageObject.height = totalHeight;
  imageObject.width = imageObject.minImageWidthEm * manager.rootUnitSizePx + POLAROID_PADDING * 2;

  setVisibilityState(imageObject, VisibilityState.APPEARING);

  return imageObject;
}

function update(imageObj: ImageObject): boolean {
  const manager = imageObj.manager;

  if (imageObj.clickedThisFrame) {
    lastClickedObject = imageObj;
  }

  if (imageObj.entityStateChanged) {
    setVisibilityState(imageObj.imageEnt, imageObj.visibilityState);
  }

  if (imageObj.imageEnt.layoutChanged) {
    if (imageObj.textObject) {
      const refEntity = manager.gameObjectIdTable[imageObj.textObject.anchoredPos.referenceId];
      const [x, y] = getEntityPositionXY(
        imageObj.textObject,
        imageObj.textObject.anchoredPos,
        refEntity,
      );
      setEntityPosition(imageObj.textObject, x, y);
    }

    imageObj.width = Math.max(
      imageObj.imageEnt.width + POLAROID_PADDING * 2,
      imageObj.minImageWidthEm * manager.rootUnitSizePx,
    );

    if (imageObj.textObject && imageObj.imageEnt.width > 0) {
      // Once the photo has real dimensions, match the caption's wrap width to the card.
      // (While loading, imageEnt.width is 0 and the card collapses to the padding, which would
      // set a zero wrap width — keep the width chosen at creation until then.)
      imageObj.textObject.width = imageObj.width - POLAROID_PADDING * 2;
    }

    imageObj.height = imageObj.imageEnt.height + POLAROID_PADDING + POLAROID_BOTTOM;
    imageObj.layoutChanged = true;
  }

  if (
    imageObj.previousImageEnt &&
    imageObj.imageEnt.visibilityState === VisibilityState.APPEARING
  ) {
    imageObj.previousImageEnt.disappearDuration = 0.4;
    setVisibilityState(imageObj.previousImageEnt, VisibilityState.DISAPPEARING);
    imageObj.previousImageEnt = null;
  }

  imageObj.width = Math.max(
    imageObj.imageEnt.width + POLAROID_PADDING * 2,
    imageObj.minImageWidthEm * manager.rootUnitSizePx,
  );

  imageObj.height = imageObj.imageEnt.height + POLAROID_PADDING + POLAROID_BOTTOM;

  if (manager.cursor.startDraggingEntityThisFrame === imageObj) {
    imageObj.liftScaleTarget = 0.06;
    const dragSign = Math.random() < 0.5 ? 1 : -1;
    imageObj.liftRotationTarget = dragSign * (Math.random() * 0.05 + 0.02);
    imageObj.liftSettleTimer = null;
  }

  if (imageObj.droppedThisFrame) {
    imageObj.liftScaleTarget = 0;
    const dropSign = Math.random() < 0.5 ? 1 : -1;
    imageObj.liftRotationTarget = dropSign * (Math.random() * 0.04 + 0.01);
    imageObj.liftSettleTimer = 0;
  }

  if (imageObj.liftSettleTimer !== null) {
    imageObj.liftSettleTimer += dt;
    if (imageObj.liftSettleTimer > 0.25) {
      imageObj.liftRotationTarget = 0;
      imageObj.liftSettleTimer = null;
    }
  }

  if (imageObj.droppedThisFrame) {
    const map = getFirstObjectByType<JourneyMap>(manager, ObjectType.JOURNEY_MAP);
    if (map && map.anchors.length > 0) {
      if (imageObj.journeyMapAnchorIndex !== null) {
        const target = JourneyMapModule.getAnchorTargetPos(map, imageObj.journeyMapAnchorIndex);
        const dx = imageObj.anchoredPos.offset.x - target.x;
        const dy = imageObj.anchoredPos.offset.y - target.y;
        const isOutsideThreshold = dx * dx + dy * dy > JOURNEY_MAP_ANCHOR_RELEASE_THRESHOLD_PX * JOURNEY_MAP_ANCHOR_RELEASE_THRESHOLD_PX;
        if (isOutsideThreshold) {
          map.anchors[imageObj.journeyMapAnchorIndex].occupiedById = null;
          imageObj.journeyMapAnchorIndex = null;
        }
      } else {
        const dxMap = Math.abs(imageObj.anchoredPos.offset.x - map.pos.x);
        const dyMap = Math.abs(imageObj.anchoredPos.offset.y - map.pos.y);
        const isOverMap = dxMap < map.width / 2 && dyMap < map.height / 2;
        if (isOverMap) {
          let bestIndex = -1;
          let bestDistSqr = Infinity;
          for (let i = 0; i < map.anchors.length; i++) {
            if (map.anchors[i].occupiedById !== null) {
              continue;
            }
            const tp = JourneyMapModule.getAnchorTargetPos(map, i);
            const dx = imageObj.anchoredPos.offset.x - tp.x;
            const dy = imageObj.anchoredPos.offset.y - tp.y;
            const distSqr = dx * dx + dy * dy;
            if (distSqr < bestDistSqr) {
              bestDistSqr = distSqr;
              bestIndex = i;
            }
          }
          if (bestIndex >= 0) {
            map.anchors[bestIndex].occupiedById = imageObj.id;
            imageObj.journeyMapAnchorIndex = bestIndex;
          }
        }
      }
    }
  }

  if (imageObj.journeyMapAnchorIndex !== null && !imageObj.dragged) {
    const map = getFirstObjectByType<JourneyMap>(manager, ObjectType.JOURNEY_MAP);
    if (map) {
      const anchor = map.anchors[imageObj.journeyMapAnchorIndex];
      const target = JourneyMapModule.getAnchorTargetPos(map, imageObj.journeyMapAnchorIndex);
      const t = Math.min(1, anchor.lerpSpeed * dt);
      imageObj.anchoredPos.offset.x = lerpNumber(imageObj.anchoredPos.offset.x, target.x, t);
      imageObj.anchoredPos.offset.y = lerpNumber(imageObj.anchoredPos.offset.y, target.y, t);
      imageObj.vel.x = 0;
      imageObj.vel.y = 0;
    }
  }

  imageObj.liftScale = lerpNumber(imageObj.liftScale, imageObj.liftScaleTarget, Math.min(1, 12 * dt));
  imageObj.liftRotation = lerpNumber(imageObj.liftRotation, imageObj.liftRotationTarget, Math.min(1, 8 * dt));

  imageObj.scale = imageObj.imageEnt.scale * (1 + imageObj.liftScale) * imageObj.baseScale;
  imageObj.rotation = imageObj.baseRotation + imageObj.liftRotation;

  const isAnimating =
    Math.abs(imageObj.liftScale - imageObj.liftScaleTarget) > 0.0005 ||
    Math.abs(imageObj.liftRotation - imageObj.liftRotationTarget) > 0.0005;

  return isAnimating;
}

export function createPackage(
  imageObject: ImageObject,
  position: V2,
  scale: number,
): RenderPackageItem[] {
  const rPackage: RenderPackageItem[] = [];

  {
    const alpha = imageObject.imageEnt.appearAlpha * 255;
    const polaroidCard: RenderPackageRect = {
      type: RenderItemType.rect,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      rotation: imageObject.rotation,
      positionX: position.x,
      positionY: position.y,
      width: imageObject.width,
      height: imageObject.height,
      shapeStyle: {
        ...SHAPE_STYLES.NONE,
        fillColor: multiplyColorAlpha255(COLORS.WHITE_100, alpha),
      },
      cornerRadius1: 4,
      cornerRadius2: 4,
      cornerRadius3: 4,
      cornerRadius4: 4,
      shadow: imageObject.dragged ? SHADOWS.SHADOW_DRAG : SHADOWS.DEFAULT,
    };
    rPackage.push(polaroidCard);
  }

  return rPackage;
}

function changeImageUri(imageObject: ImageObject, imageUri: string) {
  if (imageUri === imageObject.imageUri) return;

  const manager = imageObject.manager;
  let imageUriSized = imageUri;

  const length = API_HOST.length;

  if (imageUri.substring(0, length) === API_HOST) {
    //TODO: add image dimensions to ImageSize function
    //based on that the correct image is downloaded
    if (imageObject.maxImageWidthEm < 2 && imageObject.maxImageHeightEm < 2) {
      imageUriSized = getImageUriBySize(imageUri, 'small');
    } else if (imageObject.maxImageWidthEm < 5 && imageObject.maxImageHeightEm < 5) {
      imageUriSized = getImageUriBySize(imageUri, 'medium');
    } else {
      imageUriSized = getImageUriBySize(imageUri, 'large');
    }

    if (DataController.getImageState(imageUriSized) === '') {
      DataController.cacheImageFromApiAsync(manager.user.accessToken, imageUriSized, imageUriSized);
    }
  }

  imageObject.previousImageEnt = imageObject.imageEnt;

  imageObject.imageEnt = createImageEnt(manager, getZeroStaticEntity(), imageUriSized, {
    appearOnLoad: true,
    appearStyle: 'fade',
  });
  if (imageObject.fixedSize) {
    imageObject.imageEnt.disableAutoSize = true;
  }

  if (imageObject.serverObjId) {
    imageObject.imageEnt.unloadImageOnDispose = true;
  }
  imageObject.imageEnt.appearDuration = 0.3;
  imageObject.imageEnt.disappearDuration = 0.4;
  imageObject.imageEnt.maxWidthEm = imageObject.maxImageWidthEm;
  imageObject.imageEnt.maxHeightEm = imageObject.maxImageHeightEm;
  imageObject.imageEnt.allowDrag = true;
  imageObject.imageEnt.passDrag = true;
  imageObject.imageEnt.allowHighlight = true;
  imageObject.imageEnt.passHover = true;
  imageObject.imageEnt.clickable = true;
  imageObject.imageEnt.passClick = true;
  imageObject.imageEnt.passClickCanvas = true;

  setParent(imageObject.imageEnt, imageObject, v2());
}
